import { apiClient } from './client';
import type { TrainingBlock } from '../types/api';

export type TagCategory = 'muscle' | 'level' | 'gender' | 'type';

export interface TrainingTagDto {
  key: string;
  emoji: string;
  label: string;
  category: TagCategory;
}

/** @deprecated Use TrainingTagDto */
export type TrainingBlockTagDto = TrainingTagDto;

export interface TrainingTagsGroupedDto {
  muscles: TrainingTagDto[];
  levels: TrainingTagDto[];
  genders: TrainingTagDto[];
  types: TrainingTagDto[];
}

export function flattenTrainingTags(grouped: TrainingTagsGroupedDto): TrainingTagDto[] {
  return [
    ...grouped.muscles,
    ...grouped.levels,
    ...grouped.genders,
    ...grouped.types,
  ];
}

export const BLOCK_TAG_CATEGORIES: TagCategory[] = ['muscle', 'level', 'gender', 'type'];
export const PROGRAM_TAG_CATEGORIES: TagCategory[] = ['level', 'gender', 'type'];

export interface CreateExerciseNameRequest {
  name: string;
}

export interface CreateBlockItemRequest {
  note?: string | null;
  exercises: CreateExerciseNameRequest[];
}

export interface CreateTrainingBlockRequest {
  name: string;
  tags?: string[];
  items: CreateBlockItemRequest[];
  isSaved?: boolean;
}

export interface ParsedExercise {
  name: string;
}

export interface ParsedItem {
  note: string | null;
  exercises: ParsedExercise[];
}

export interface ParsedTrainingBlock {
  name: string;
  tags: string[];
  items: ParsedItem[];
}

export interface ParseBlockResponse {
  sessionId: string;
  result: ParsedTrainingBlock;
}

export interface TrainingBlockProgramUsage {
  id: number;
  name: string;
  isActive: boolean;
  isSaved: boolean;
}

export interface TrainingBlockUsage {
  programs: TrainingBlockProgramUsage[];
}

export const trainingBlockApi = {
  async getTags(): Promise<TrainingTagsGroupedDto> {
    return apiClient.get<TrainingTagsGroupedDto>('/v1/training-blocks/tags');
  },

  async getBlocks(): Promise<TrainingBlock[]> {
    return apiClient.get<TrainingBlock[]>('/v1/training-blocks');
  },

  async getBlock(id: number): Promise<TrainingBlock> {
    return apiClient.get<TrainingBlock>(`/v1/training-blocks/${id}`);
  },

  async getBlockUsage(id: number): Promise<TrainingBlockUsage> {
    return apiClient.get<TrainingBlockUsage>(`/v1/training-blocks/${id}/usage`);
  },

  async createBlock(request: CreateTrainingBlockRequest): Promise<TrainingBlock> {
    return apiClient.post<TrainingBlock>('/v1/training-blocks', request);
  },

  async updateBlock(id: number, request: CreateTrainingBlockRequest): Promise<TrainingBlock> {
    return apiClient.put<TrainingBlock>(`/v1/training-blocks/${id}`, request);
  },

  async copyBlock(id: number): Promise<TrainingBlock> {
    return apiClient.post<TrainingBlock>(`/v1/training-blocks/${id}/copy`);
  },

  async getExerciseSuggestions(q: string): Promise<string[]> {
    return apiClient.get<string[]>(`/v1/training-blocks/exercises/suggestions?q=${encodeURIComponent(q)}`);
  },

  async deleteBlock(id: number): Promise<void> {
    return apiClient.delete<void>(`/v1/training-blocks/${id}`);
  },

  async saveBlock(id: number): Promise<TrainingBlock> {
    return apiClient.post<TrainingBlock>(`/v1/training-blocks/${id}/save`);
  },

  async parseBlock(rawText: string): Promise<ParseBlockResponse> {
    return apiClient.post<ParseBlockResponse>('/v1/training-blocks/parse', { rawText });
  },

  async reparseBlock(
    sessionId: string,
    currentBlock: ParsedTrainingBlock,
    comment: string
  ): Promise<ParseBlockResponse> {
    return apiClient.post<ParseBlockResponse>('/v1/training-blocks/reparse', {
      sessionId,
      currentBlock,
      comment,
    });
  },
};
